import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthService from '../../services/auth.service';
import { useDispatch } from "react-redux";
import { setUserInfo } from "../../redux/reducers/authSlice";
import { validateLoginCode } from '../../utils/Tools';
import { PARENT_DASHBOARD_DISMISS_ADD_CHILD_KEY } from '../../constants/sessionStorageKeys';

interface RedirectState {
    loading: boolean;
    error: string | null;
    hasProcessed: boolean;
}

function Redirect() {
    const navigate = useNavigate();
    const authService = new AuthService();
    const dispatch = useDispatch();
    const [state, setState] = useState<RedirectState>({
        loading: true,
        error: null,
        hasProcessed: false
    });

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    const handleLogin = async (values: any) => {
        try {
            setState(prev => ({ ...prev, loading: true, error: null }));

            const response = await authService.adminSignIn(values);

            if (!response.status) {
                const errorMsg = response?.message || "Authentication failed";
                toast.error(errorMsg);
                setState(prev => ({
                    ...prev,
                    loading: false,
                    error: errorMsg
                }));
                return;
            }

            toast.success(response?.message || "Login successful");
            localStorage.setItem('ryd-parent-token', response?.data?.token);
            dispatch(setUserInfo(response?.data));
            try {
                sessionStorage.removeItem(PARENT_DASHBOARD_DISMISS_ADD_CHILD_KEY);
            } catch {
                /* ignore */
            }

            setTimeout(() => {
                navigate("/parent/home");
            }, 1000);

            // window.location.reload()

        } catch (err: any) {
            const errorMsg = err?.message || "Network error occurred";
            toast.error(errorMsg);
            setState(prev => ({
                ...prev,
                loading: false,
                error: errorMsg
            }));
        }
    };

    const handleRetry = () => {
        setState({
            loading: true,
            error: null,
            hasProcessed: false
        });
    };

    useEffect(() => {
        if (!code && !state.hasProcessed) {
            setState(prev => ({
                ...prev,
                loading: false,
                error: "No authorization code found in URL",
                hasProcessed: true
            }));
            return;
        }

        if (code && !state.hasProcessed) {
            try {
                const payload = validateLoginCode(code);
                console.log("login payload:", payload);

                if (!payload.status) {
                    const errorMsg = "Invalid authorization code";
                    toast.error(errorMsg);
                    setState(prev => ({
                        ...prev,
                        loading: false,
                        error: errorMsg,
                        hasProcessed: true
                    }));
                    return;
                }

                if (payload.decoded && payload.status) {
                    setState(prev => ({ ...prev, hasProcessed: true }));
                    handleLogin(payload.decoded);
                }
            } catch (error: any) {
                const errorMsg = "Failed to process authorization code";
                toast.error(errorMsg);
                setState(prev => ({
                    ...prev,
                    loading: false,
                    error: errorMsg,
                    hasProcessed: true
                }));
            }
        }
    }, [code, state.hasProcessed]);

    const ErrorSVG = () => (
        <svg
            className="w-16 h-16 text-red-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
        </svg>
    );

    const LoadingSpinner = () => (
        <div className="w-16 h-16 border-4 border-[#A8518A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
    );

    if (state.error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center max-w-md mx-auto px-6">
                    <ErrorSVG />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Authentication Error
                    </h2>
                    <p className="text-gray-600 mb-6">
                        {state.error}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            onClick={handleRetry}
                            className="px-4 py-2 bg-[#A8518A] text-white rounded-md hover:bg-[#8f4575] transition-colors"
                        >
                            Try Again
                        </button>
                        {/* <button
                            onClick={() => navigate('/login')}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Back to Login
                        </button> */}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="text-center">
                <LoadingSpinner />
                <p className="text-gray-600">
                    {state.loading ? "Authenticating..." : "Processing..."}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                    Please wait while we verify your credentials
                </p>
            </div>
        </div>
    );
}

export default Redirect;